import React from 'react';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';
import Lottie from 'lottie-react';
import AnimatedText from '../AnimatedText';
import TTStatsVerified from '../TTStatsVerified';
import { getVerificationStatus } from '../../config/verifiedUsers';
import { getClassificationForElo, getClassificationColor } from '../../utils/classificationUtils';
import { EloData } from '../../types/api.types';

interface PlayerHeaderProps {
  profileAnimation: any;
  playerData: EloData;
  url?: string;
  isLikelyFemale: boolean;
  currentLang: 'en' | 'fr' | 'de' | 'it';
  isFavorite: (url: string) => boolean;
  handleFavoriteToggle: () => void;
  t: {
    license: string;
  };
}

/**
 * PlayerHeader Component
 * Displays player profile animation, name, verification badge, classification, rank, and favorite button
 */
const PlayerHeader: React.FC<PlayerHeaderProps> = ({
  profileAnimation,
  playerData,
  url,
  isLikelyFemale,
  currentLang,
  isFavorite,
  handleFavoriteToggle,
  t
}) => {
  // Convert "LastName, FirstName" to "FirstName LastName"
  const formatPlayerName = (name: string | undefined) => {
    if (!name || name === 'temp' || name === 'Unknown' || name === 'Unknown Player') {
      return url ? decodeURIComponent(url).split('/').pop()?.replace(/-/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Player' : 'Player';
    }

    if (name.includes(',')) {
      const [lastName, firstName] = name.split(',').map(s => s.trim());
      return `${firstName} ${lastName}`;
    }

    return name;
  };

  const displayName = formatPlayerName(playerData.player_name);

  const playerNameForVerification = playerData.player_name && playerData.player_name !== 'temp' && playerData.player_name !== 'Unknown' && playerData.player_name !== 'Unknown Player'
    ? playerData.player_name
    : url ? decodeURIComponent(url).split('/').pop()?.replace(/-/g, ' ') || '' : '';

  const verificationType = getVerificationStatus(playerNameForVerification, url, playerData.licenseNumber);

  const getFavoriteText = (isFav: boolean) => {
    if (isFav) {
      return currentLang === 'fr' ? 'Favoris' :
             currentLang === 'de' ? 'Favorit' :
             currentLang === 'it' ? 'Preferito' :
             'Favorited';
    }
    return currentLang === 'fr' ? 'Ajouter' :
           currentLang === 'de' ? 'Hinzufügen' :
           currentLang === 'it' ? 'Aggiungi' :
           'Favorite';
  };

  return (
    <div className="text-center mb-16">
      {/* Lottie Profile Animation */}
      {profileAnimation && (
        <motion.div
          className="flex justify-center mb-6"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.5, ease: "easeOut" }}>
          <div className="w-32 h-32 md:w-40 md:h-40">
            <Lottie
              animationData={profileAnimation}
              loop={true}
              autoplay={true}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        </motion.div>
      )}

      {/* Player name section */}
      <div className="mb-4 px-4 md:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-3">
            <AnimatedText
              text={displayName}
              className="text-4xl sm:text-5xl md:text-6xl font-extralight text-gray-900 dark:text-gray-100 break-words leading-tight transition-colors duration-300"
              delay={0.2}
              staggerDelay={0.03}
            />
            {verificationType && (
              <TTStatsVerified
                size="md"
                delay={0.8}
                lang={currentLang}
                type={verificationType}
              />
            )}
          </div>
        </div>
      </div>

      {/* Badges section - Rank, License, and Favorite */}
      <motion.div
        className="flex items-center justify-center gap-2 flex-wrap mb-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.5 }}>

        {/* Show dual classification badge for women */}
        {isLikelyFemale && (() => {
          const womenClass = getClassificationForElo(playerData.current_elo, true);
          const menClass = getClassificationForElo(playerData.current_elo, false);
          const displayClass = `${womenClass} / ${menClass}`;
          const colors = getClassificationColor(womenClass);

          return (
            <motion.span
              className="px-3 py-1.5 rounded-lg text-sm font-bold"
              style={{
                backgroundColor: colors.bg,
                color: colors.text
              }}
              initial={{ opacity: 0, scale: 0.8, x: -20 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.6, type: "spring", stiffness: 200 }}>
              {displayClass}
            </motion.span>
          );
        })()}

        {playerData.swissRank && (
          <motion.span
            className="px-3 py-1.5 rounded-lg text-sm font-bold bg-gray-600 dark:bg-gray-700 text-white transition-colors duration-300"
            initial={{ opacity: 0, scale: 0.8, x: -20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.6, type: "spring", stiffness: 200 }}>
            #{playerData.swissRank}
          </motion.span>
        )}

        {playerData.licenseNumber && (
          <motion.span
            className="px-3 py-1.5 rounded-lg text-sm font-bold bg-gray-600 dark:bg-gray-700 text-white transition-colors duration-300"
            initial={{ opacity: 0, scale: 0.8, x: -20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.7, type: "spring", stiffness: 200 }}>
            {t.license}: {playerData.licenseNumber}
          </motion.span>
        )}

        {/* Favorite button badge */}
        <motion.button
          onClick={handleFavoriteToggle}
          className={`px-3 py-1.5 rounded-lg text-sm font-bold text-white flex items-center gap-1.5 transition-colors duration-300 ${
            url && isFavorite(url)
              ? 'bg-rose-500 hover:bg-rose-600 dark:bg-rose-600 dark:hover:bg-rose-700'
              : 'bg-gray-600 hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-800'
          }`}
          initial={{ opacity: 0, scale: 0.8, x: -20 }}
          animate={{
            opacity: 1,
            scale: 1,
            x: 0,
          }}
          transition={{ duration: 0.3, delay: 0.7, type: "spring", stiffness: 200 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.div
            animate={url && isFavorite(url) ? {
              scale: [1, 1.3, 1],
              rotate: [0, -10, 10, -10, 0]
            } : {}}
            transition={{ duration: 0.5 }}
          >
            <Heart className={`w-4 h-4 ${url && isFavorite(url) ? 'fill-white' : ''}`} />
          </motion.div>
          <span>
            {getFavoriteText(url ? isFavorite(url) : false)}
          </span>
        </motion.button>
      </motion.div>

      {/* Club name */}
      {playerData.club && (
        <AnimatedText
          text={playerData.club}
          className="text-gray-500 dark:text-gray-400 text-sm mb-4 transition-colors duration-300"
          delay={0.8}
          staggerDelay={0.015}
        />
      )}
    </div>
  );
};

export default PlayerHeader;
